import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ImageSmallIcon from '@/widgets/UploadFilesWidgets/components/ImageSmallIcon';
import { Loader, MoreHorizontal } from 'lucide-react';
import moment from 'moment';
import { useNavigate } from 'react-router';

import { AgreementDTO } from '@/types/api-types';
import {
  BreadcrumbItemType,
  NormalizedFile,
} from '@/types/file-explorer-types';

import IconCheckbox from '@/assets/svg/icon-check.svg?react';
import IconDoc from '@/assets/svg/icon-docx.svg?react';
import IconFile from '@/assets/svg/icon-file.svg?react';
import IconGrid from '@/assets/svg/icon-grid.svg?react';
import IconImgHeic from '@/assets/svg/icon-heic.svg?react';
import IconNewPage from '@/assets/svg/icon-img-new-page.svg?react';
import IconImg from '@/assets/svg/icon-img.svg?react';
import IconList from '@/assets/svg/icon-list.svg?react';
import IconVideo from '@/assets/svg/icon-mp4.svg?react';
import IconPdf from '@/assets/svg/icon-pdf.svg?react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useAuthStore } from '@/stores/authStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useGlobalStore } from '@/stores/globalStore';

import { apiService } from '@/services/api-service';

import { getFileType, USER_ROLES } from '@/constants/global-constants';
import { executorRoutes } from '@/constants/route-settings';
import { ExplorerItemType } from '@/constants/table-columns-config';

import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import DetailsFileInfo from '../DetailsPanel/DetailsFileInfo';
import BreadCrumbsComponent from './BreadCrumbsComponent';
import { ImageCompareDialog } from './ImageCompareDialog';
import MoveFileView from './MoveFileView';
import { PhotoViewer } from './PhotoViewer';

const INITIAL_LOAD_SIZE = 50;
const LOAD_MORE_SIZE = 50;

interface MediaViewProps {
  items: NormalizedFile[];
  isSmall?: boolean;
  agreementId?: string;
  isLoading?: boolean;
  breadcrumbs: BreadcrumbItemType[];
  onNavigate?: (params: { uuid: string; entityType: ExplorerItemType }) => void;
  setCurrentUUID: (uuid: string) => void;
  setCurrentEntityType: (type: ExplorerItemType) => void;
  searchTerm?: string;
  refetchData?: (pagination?: { pageIndex: number; pageSize: number }) => void;
}

export const MediaView: FC<MediaViewProps> = ({
  items,
  isSmall,
  agreementId,
  isLoading,
  breadcrumbs,
  onNavigate,
  setCurrentUUID,
  setCurrentEntityType,
  searchTerm,
  refetchData,
}) => {
  // Local state
  const [isDetailsFileInfoOpen, setIsDetailsFileInfoOpen] = useState(false);
  const [fileInfo, setFileInfo] = useState<NormalizedFile | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [isCompareDialogOpen, setIsCompareDialogOpen] = useState(false);
  const { user } = useAuthStore();

  const {
    setFileUuidToMove,
    setIsMoveFileDialogOpen,
    users,
    getAutoFetchUsers,
  } = useGlobalStore();

  const { setAgreements, agreements } = useApiManagerStore();
  const {
    filesState,
    clearFilesCache,
    removeFileFromCache,
    mediaViewMode,
    setMediaViewMode,
  } = useFileExplorerStore();

  const [deleteFileId, setDeleteFileId] = useState('');
  const [fileType, setFileType] = useState<string>('all');
  const [viewErrorFile, setViewErrorFile] = useState<NormalizedFile | null>(
    null,
  );
  const navigate = useNavigate();

  const { deleteFile } = useApiManagerStore();

  // Infinite scroll state
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [lastProcessedItemsLength, setLastProcessedItemsLength] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Get cached files from store
  const cachedFiles = useMemo(() => {
    if (!agreementId) return [];
    return filesState.rootIds
      .map((id) => filesState.entities[id])
      .filter((file) => file?.agreement === agreementId);
  }, [filesState.rootIds, filesState.entities, agreementId]);

  // Clear cache when agreement changes
  useEffect(() => {
    if (agreementId) {
      clearFilesCache(agreementId);
      setCurrentPage(0);
      setHasMoreData(true);
      setIsLoadingMore(false);
      setLastProcessedItemsLength(0);
    }
  }, [agreementId]);

  // Update pagination state when new items arrive (prevent multiple triggers)
  useEffect(() => {
    if (items.length > 0 && items.length !== lastProcessedItemsLength) {
      const currentCachedCount = cachedFiles.length;
      const newItemsCount = items.length - currentCachedCount;

      if (newItemsCount > 0) {
        // Check if we got fewer items than requested (indicates end of data)
        const expectedItems =
          currentPage === 0 ? INITIAL_LOAD_SIZE : LOAD_MORE_SIZE;
        const hasMore = newItemsCount >= expectedItems;
        setHasMoreData(hasMore);
      }

      setLastProcessedItemsLength(items.length);
    }
  }, [items.length, cachedFiles.length, currentPage, lastProcessedItemsLength]);

  // Use cached files for display
  const displayFiles = cachedFiles.length > 0 ? cachedFiles : items;

  // Deduplicate items to prevent duplicate keys
  const filteredItems = useMemo(() => {
    const uniqueItems = displayFiles.reduce((acc, item) => {
      if (!acc.find((existingItem) => existingItem.uuid === item.uuid)) {
        acc.push(item);
      }
      return acc;
    }, [] as NormalizedFile[]);

    return uniqueItems.filter((item) => {
      if (fileType === 'all') return true;
      if (fileType === 'photo')
        return item.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);
      if (fileType === 'video')
        return item.name.toLowerCase().match(/\.(mp4|webm|mov|avi)$/);
      if (fileType === 'document')
        return item.name.toLowerCase().match(/\.(doc|docx|pdf|txt)$/);
      return true;
    });
  }, [displayFiles, fileType]);

  // Fetch users for file authors
  useEffect(() => {
    const userUuids = [
      ...new Set(filteredItems.map((item) => item.user).filter(Boolean)),
    ];
    if (userUuids.length > 0) {
      getAutoFetchUsers(userUuids);
    }
  }, [filteredItems, getAutoFetchUsers]);

  // Get user full name with fallback
  const getUserFullName = useCallback(
    (uuid: string) => {
      const foundUser = (users ?? []).find((u) => u.uuid === uuid);
      if (!foundUser) return '-';
      const first = foundUser.first_name || '';
      const last = foundUser.last_name || '';
      return `${first} ${last}`.trim() || '-';
    },
    [users],
  );

  // Load more data function with duplicate prevention
  const loadMoreData = useCallback(async () => {
    if (!refetchData || isLoadingMore || !hasMoreData) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const nextPage = currentPage + 1;
      await refetchData({
        pageIndex: nextPage,
        pageSize: LOAD_MORE_SIZE,
      });
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Error loading more files:', error);
      setHasMoreData(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [refetchData, isLoadingMore, hasMoreData, currentPage]);

  // Debounced infinite scroll detection
  useEffect(() => {
    if (!hasMoreData || isLoadingMore) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      // Clear previous timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Debounce scroll detection
      scrollTimeout = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

        // Load more when user scrolls to 85% of the content (increased threshold)
        if (scrollPercentage > 0.85 && hasMoreData && !isLoadingMore) {
          loadMoreData();
        }
      }, 150); // 150ms debounce
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
      };
    }
  }, [loadMoreData, hasMoreData, isLoadingMore]);

  const handleNavigateBreadcrumbs = (uuid: string, type: ExplorerItemType) => {
    onNavigate?.({
      uuid,
      entityType: type,
    });
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentImageIndex((prev) => Math.max(0, prev - 1));
    } else {
      setCurrentImageIndex((prev) =>
        Math.min(filteredItems.length - 1, prev + 1),
      );
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    setDeleteFileId('');
    try {
      await deleteFile(fileId);

      // Remove file from store cache
      removeFileFromCache(fileId);

      const filename = displayFiles.find((item) => item.uuid === fileId)?.name;
      if (agreementId) {
        const newComment = await apiService.createComment({
          agreement: agreementId,
          content: `Файл ${filename} видалено`,
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          user: user?.uuid || '',
          is_edited: false,
          is_system_comment: true,
        });

        const foundAgreement = agreements.find((a) => a.uuid === agreementId);

        if (foundAgreement) {
          setAgreements([
            {
              ...foundAgreement,
              comments: [newComment.uuid, ...(foundAgreement.comments || [])],
            } as AgreementDTO,
            ...agreements.filter((a) => a.uuid !== agreementId),
          ]);
        }
      }
    } catch (error) {}
  };

  const handleAddFiles = () => {
    if (agreementId) {
      navigate(`${executorRoutes.uploadFiles}/${agreementId}`);
    }
  };

  const handleEditFile = async (file: NormalizedFile | null) => {
    if (!file) return;
    setFileInfo(file);
  };

  useEffect(() => {
    return () => {
      setFileInfo(null);
      setIsDetailsFileInfoOpen(false);
    };
  }, []);

  const handleDownload = async (fileLink: string, fileName: string) => {
    try {
      const response = await fetch(fileLink);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {}
  };

  const currentFileImage = filteredItems[currentImageIndex]
    ? {
        ...filteredItems[currentImageIndex],
        transform: { scale: 1, rotation: 0 },
        onTransform: () => {},
      }
    : null;
  const openInNewTab = (items: NormalizedFile[], currentIndex: number) => {
    const win = window.open('/viewer.html', '_blank');
    if (!win) return;

    const onReady = (e: MessageEvent) => {
      if (e.data?.type === 'viewer_ready') {
        win.postMessage(
          {
            type: 'init_viewer',
            payload: { items, currentIndex },
          },
          '*',
        );
        window.removeEventListener('message', onReady);
      }
    };

    window.addEventListener('message', onReady);
  };

  const renderFileContent = (item: NormalizedFile, index: number) => {
    let fileTypeFile = getFileType(item.name);

    if (!fileTypeFile) {
      return null;
    }

    switch (fileTypeFile) {
      case 'VIDEO':
        return (
          <div
            className="relative z-10 h-auto min-h-[130px] w-full cursor-pointer content-center text-center transition-opacity hover:opacity-90 sm:min-h-[200px] sm:w-[240px]"
            onClick={() => {
              setCurrentImageIndex(index);
              setIsPhotoViewerOpen(true);
            }}
          >
            <video
              controls={false}
              className="relative z-0 h-full w-full object-contain"
            >
              <source
                src={item.file_link ?? ''}
                type={`video/${item.name.split('.').pop()}`}
              />
              Ваш браузер не підтримує відео.
            </video>
          </div>
        );

      case 'PDF':
        return (
          <div
            className="flex h-auto min-h-[130px] w-full cursor-pointer content-center items-center justify-center text-center transition-opacity hover:opacity-90 sm:min-h-[200px] sm:w-[240px]"
            onClick={() => {
              setCurrentImageIndex(index);
              setIsPhotoViewerOpen(true);
            }}
          >
            <IconPdf className="h-20 w-20 fill-dark-red" />
          </div>
        );

      case 'DOCUMENT':
        return (
          <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-sm sm:h-[200px]">
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 sm:w-[240px]">
              <IconDoc className="h-20 w-20" />
            </div>
          </div>
        );

      case 'IMAGE':
        return (
          <div className="relative z-10 h-full w-full [&_button]:hover:opacity-90">
            <button
              className="absolute right-2 top-2 z-[1000] p-2 opacity-35 transition-opacity"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setCurrentImageIndex(index);
                openInNewTab(filteredItems, index);
              }}
            >
              <IconNewPage className="h-5 w-5" />
            </button>
            <img
              src={item.thumbnail_url || item.webp_url || item.file_link || ''}
              alt={item.name}
              className="relative z-10 h-auto min-h-[130px] w-full cursor-pointer content-center text-center transition-opacity hover:opacity-90 sm:min-h-[200px] sm:w-[240px]"
              onClick={() => {
                setCurrentImageIndex(index);
                setIsPhotoViewerOpen(true);
              }}
              onError={(e) => {
                // fallback chain: thumbnail -> webp -> file_link -> clear
                const target = e.currentTarget as HTMLImageElement;
                const webpUrl = item.webp_url || '';
                const fileLink = item.file_link || '';
                
                if (target.src !== webpUrl && webpUrl) {
                  target.src = webpUrl;
                } else if (target.src !== fileLink && fileLink) {
                  target.src = fileLink;
                } else {
                  target.src = '';
                }
              }}
            />
          </div>
        );

      case 'IMAGE_HEIC':
        return (
          <div className="flex h-auto min-h-[130px] w-full content-center items-center justify-center text-center transition-opacity hover:opacity-90 sm:min-h-[200px] sm:w-[240px]">
            <IconImg className="h-20 w-20 fill-dark-red" />
          </div>
        );

      default:
        return (
          <div className="flex h-auto min-h-[130px] w-full content-center items-center justify-center text-center transition-opacity hover:opacity-90 sm:min-h-[200px] sm:w-[240px]">
            <IconPdf className="h-20 w-20 fill-gray-200" />
          </div>
        );
    }
  };

  const renderFileIcon = (fileName: string) => {
    let fileTypeFile = getFileType(fileName);

    if (!fileTypeFile) {
      return <IconFile className="h-[16px] w-[16px] fill-dark-red" />;
    }

    switch (fileTypeFile) {
      case 'VIDEO':
        return <IconVideo className="h-[16px] w-[16px] fill-dark-red" />;
      case 'PDF':
        return <IconPdf className="h-[16px] w-[16px] fill-dark-red" />;
      case 'DOCUMENT':
        return <IconDoc className="h-[16px] w-[16px] fill-dark-red" />;
      case 'IMAGE':
        return <IconImg className="h-[16px] w-[16px] fill-dark-red" />;
      case 'IMAGE_HEIC':
        return <IconImgHeic className="h-[16px] w-[16px] fill-dark-red" />;

      default:
        return <IconFile className="h-[16px] w-[16px] fill-dark-red" />;
    }
  };

  // Reusable action dropdown for both grid and list views
  const renderActionDropdown = (item: NormalizedFile) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          aria-label="File actions"
        >
          <MoreHorizontal className="h-5 w-5 text-primary" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => {
            setFileInfo(item);
            setIsDetailsFileInfoOpen(true);
          }}
        >
          Інформація про файл
        </DropdownMenuItem>
        {item.validation_errors && item.validation_errors.length > 0 && (
          <DropdownMenuItem onClick={() => setViewErrorFile(item)}>
            Переглянути помилку
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => handleDownload(item.file_link ?? '', item.name)}
        >
          Завантажити
        </DropdownMenuItem>
        {user?.role === USER_ROLES.CONTROLLER.value && (
          <>
            <DropdownMenuItem
              onClick={() => {
                setFileUuidToMove(item.uuid);
                setIsMoveFileDialogOpen(true);
              }}
            >
              Перемістити
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteFileId(item.uuid)}
            >
              Видалити
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Grid view rendering
  const renderGridView = () => (
    <div className="flex flex-wrap gap-4 p-5 sm:gap-6 sm:p-[36px]">
      {filteredItems.map((item, index) => (
        <div
          key={item.uuid}
          className={cn(
            'relative flex w-[calc(50%-8px)] flex-col items-center justify-center rounded-md sm:max-h-[260px] sm:max-w-[260px] sm:flex-none sm:p-2.5',
            item.validation_errors && item.validation_errors.length > 0
              ? 'bg-[#FFF5F5]'
              : 'bg-blue-50',
          )}
          aria-label={`File: ${item.name}`}
        >
          <div className="flex w-full items-center justify-between pb-[14px] pl-[10px] pt-[6px]">
            {renderFileIcon(item.name)}
            <span
              title={item.name}
              className="font-montserrat sm:max-w-auto ml-1 max-w-[120px] truncate text-ellipsis pr-1 text-sm font-normal text-primary"
            >
              {item.name}
            </span>
            {renderActionDropdown(item)}
          </div>
          <div className="flex h-full items-center justify-center overflow-hidden rounded-sm sm:h-auto">
            {renderFileContent(item, index)}
          </div>
        </div>
      ))}
    </div>
  );

  // List view rendering
  const renderListView = () => (
    <div className="overflow-x-auto">
      <table className={cn(
        "w-full border-separate border-spacing-0",
        !isDetailsFileInfoOpen && "sm:min-w-[900px]"
      )}>
        <thead>
          <tr>
            <th className="border-b px-4 py-5 text-left text-base font-semibold sm:pl-9">
              Назва
            </th>
            <th className="border-b px-4 py-5 text-left text-base font-semibold">
              Розмір
            </th>
            <th className="border-b px-4 py-5 text-left text-base font-semibold">
              Автор
            </th>
            <th className="border-b px-4 py-5 text-left text-base font-semibold">
              Знято
            </th>
            <th className="border-b px-4 py-5 text-left text-base font-semibold">
              Завантажено
            </th>
            <th className="border-b px-4 py-5 text-right text-base font-semibold sm:pr-9">
              Дія
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredItems.map((item, index) => (
            <tr
              key={item.uuid}
              className={cn(
                'cursor-pointer border-b border-gray-200 hover:bg-neutral-50',
                item.validation_errors && item.validation_errors.length > 0
                  ? 'bg-[#FFF5F5]'
                  : index % 2 === 1 && 'bg-red-50/30',
              )}
              onClick={() => {
                setCurrentImageIndex(index);
                setIsPhotoViewerOpen(true);
              }}
            >
              <td className="border-b border-separator px-4 py-3 sm:pl-9">
                <div className="flex items-center gap-3">
                  {getFileType(item.name) === 'IMAGE' && item.thumbnail_url ? (
                    <ImageSmallIcon fileLink={item.thumbnail_url} />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center">
                      {renderFileIcon(item.name)}
                    </div>
                  )}
                  <span
                    className="max-w-[200px] truncate text-base font-medium"
                    title={item.name}
                  >
                    {item.name}
                  </span>
                </div>
              </td>
              <td className="whitespace-nowrap border-b border-separator px-4 py-3 text-base font-medium">
                {item.size}
              </td>
              <td className="whitespace-nowrap border-b border-separator px-4 py-3 text-base font-medium">
                {getUserFullName(item.user)}
              </td>
              <td className="whitespace-nowrap border-b border-separator px-4 py-3 text-base font-medium">
                {item.filmed_date
                  ? moment(item.filmed_date).format('DD.MM.YYYY HH:mm')
                  : '-'}
              </td>
              <td className="whitespace-nowrap border-b border-separator px-4 py-3 text-base font-medium">
                {item.upload_date
                  ? moment(item.upload_date).format('DD.MM.YYYY HH:mm')
                  : item.created
                    ? moment(item.created).format('DD.MM.YYYY HH:mm')
                    : '-'}
              </td>
              <td
                className="border-b border-separator px-4 py-3 text-right sm:pr-9"
                onClick={(e) => e.stopPropagation()}
              >
                {renderActionDropdown(item)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div
        className={cn(
          `relative flex h-full flex-1 flex-col gap-1 rounded-md bg-white transition-all duration-300 ease-in-out`,
          isDetailsFileInfoOpen
            ? 'h-full sm:mr-[574px] sm:w-[calc(100%-574px)]'
            : '',
          isSmall ? '' : 'overflow-hidden shadow-sm',
        )}
      >
        <BreadCrumbsComponent
          breadcrumbs={breadcrumbs}
          isSmall={isSmall}
          searchTerm={searchTerm}
          setCurrentUUID={setCurrentUUID}
          setCurrentEntityType={setCurrentEntityType}
          handleNavigate={handleNavigateBreadcrumbs}
          totalDocuments={0}
          isRootLevel={false}
        />
        <div className="flex-grow overflow-auto">
          <div
            className={cn('flex h-full flex-col transition-all duration-300')}
          >
            <PhotoViewer
              items={filteredItems}
              currentIndex={currentImageIndex}
              onNavigate={handleNavigate}
              isPhotoViewerOpen={isPhotoViewerOpen}
              setIsPhotoViewerOpen={setIsPhotoViewerOpen}
              setIsCompareDialogOpen={setIsCompareDialogOpen}
            />
            <ImageCompareDialog
              currentUUID={agreementId!}
              isOpen={isCompareDialogOpen}
              onClose={() => setIsCompareDialogOpen(false)}
              initialLeftImageUUID={currentFileImage?.uuid}
            />
            <MoveFileView
              agreementId={agreementId || ''}
              fileName={filteredItems[currentImageIndex]?.name || ''}
            />
            <ConfirmationDialog
              isOpen={!!deleteFileId}
              onClose={() => setDeleteFileId('')}
              onConfirm={() => handleDeleteFile(deleteFileId)}
              title="Ви впевнені, що хочете видалити файл?"
              confirmText="Видалити"
              cancelText="Скасувати"
              variant="destructive"
            />
            {isSmall ? null : (
              <div className="flex flex-shrink-0 items-center justify-between border-b border-separator pb-[22px] pl-9 pr-9 pt-[18px]">
                <div className="flex items-center gap-6">
                  <Select value={fileType} onValueChange={setFileType}>
                    <SelectTrigger className="h-[36px] w-auto gap-2 rounded-sm p-1 sm:p-0">
                      <SelectValue placeholder="Тип" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <span>Всі</span>
                      </SelectItem>
                      <SelectItem value="photo">
                        <span>Зображення</span>
                      </SelectItem>
                      <SelectItem value="video">
                        <span>Відео</span>
                      </SelectItem>
                      <SelectItem value="document">
                        <span>Документи</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={handleAddFiles}>
                    Додати файли
                  </Button>
                </div>
                <div className="flex items-center overflow-hidden rounded-full border border-gray-200">
                  <button
                    onClick={() => setMediaViewMode('grid')}
                    className={cn(
                      'flex items-center justify-center border-r border-gray-200 py-2.5 pl-4 pr-3 transition-colors',
                      mediaViewMode === 'grid'
                        ? 'bg-blue-50 text-primary'
                        : 'px-5 hover:bg-gray-50',
                    )}
                    aria-label="Grid view"
                    aria-pressed={mediaViewMode === 'grid'}
                  >
                    {mediaViewMode === 'grid' ? (
                      <IconCheckbox className="mr-1 h-3 w-3" />
                    ) : null}
                    <IconGrid className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setMediaViewMode('list')}
                    className={cn(
                      'flex items-center justify-center py-2.5 pl-3 pr-4 transition-colors',
                      mediaViewMode === 'list'
                        ? 'bg-blue-50 text-primary'
                        : 'px-5 hover:bg-gray-50',
                    )}
                    aria-label="List view"
                    aria-pressed={mediaViewMode === 'list'}
                  >
                    {mediaViewMode === 'list' ? (
                      <IconCheckbox className="mr-1 h-3 w-3" />
                    ) : null}
                    <IconList className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
            <div ref={scrollContainerRef} className="flex-grow overflow-auto">
              {isLoading && filteredItems.length === 0 ? (
                <div className="flex h-full w-full items-center justify-center">
                  <Loader className="h-8 w-8 animate-spin-slow" />
                </div>
              ) : (
                <>
                  {mediaViewMode === 'grid'
                    ? renderGridView()
                    : renderListView()}
                  {isLoadingMore && (
                    <div className="flex items-center justify-center p-6">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin h-6 w-6 rounded-full border-b-2 border-primary"></div>
                        <span className="text-sm text-gray-600">
                          Завантаження...
                        </span>
                      </div>
                    </div>
                  )}
                  {!hasMoreData && !isLoading && filteredItems.length > 0 && (
                    <div className="flex items-center justify-center p-6">
                      <span className="text-sm text-gray-500">
                        Немає більше файлів для завантаження
                      </span>
                    </div>
                  )}
                  {!isLoading && filteredItems.length === 0 && (
                    <div className="flex h-full w-full items-center justify-center">
                      <span className="text-gray-500">
                        Немає файлів для відображення
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {fileInfo !== null && (
        <DetailsFileInfo
          isOpen={isDetailsFileInfoOpen}
          onClose={() => {
            setIsDetailsFileInfoOpen(false);
            setFileInfo(null);
          }}
          data={fileInfo}
          setFileInfo={handleEditFile}
        />
      )}
      <Dialog
        open={!!viewErrorFile}
        onOpenChange={() => setViewErrorFile(null)}
      >
        <DialogContent className="w-full max-w-[400px] p-5 pb-7">
          <DialogHeader>
            <DialogTitle className="mb-6 text-2xl font-semibold">
              Помилка
            </DialogTitle>
            <DialogDescription className="space-y-2 text-base text-primary">
              {viewErrorFile?.validation_errors?.map((error, i) => (
                <p key={i}>{error}</p>
              ))}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};
